// The consent page. Claude (on claude.ai, or Claude Code) sends the admin's
// browser here by way of /oauth/authorize; allowing it sends the browser back
// with a code Claude trades for a token. Only the admin can answer, so anyone
// else — signed out, or a member — is told so and nothing more.
import { Link, useSearchParams } from "wouter";
import { Header } from "@/components/Header";
import { useUser } from "@/lib/auth";
import { useOAuthDecision, useOAuthRequest } from "@/lib/oauth";

export function Connect() {
  const [params] = useSearchParams();
  const token = params.get("request") ?? "";
  const { data: user, isLoading } = useUser();
  const isAdmin = user?.role === "admin";
  const request = useOAuthRequest(token, isAdmin);
  const decide = useOAuthDecision(token);

  const answer = async (approve: boolean) => {
    try {
      const { redirect } = await decide.mutateAsync(approve);
      window.location.assign(redirect);
    } catch {
      /* shown via decide.error */
    }
  };

  const here = `/connect?request=${encodeURIComponent(token)}`;
  let body;
  if (isLoading) body = null;
  else if (!user) {
    body = (
      <>
        <h1 className="font-serif text-[28px] font-semibold">Connect Claude</h1>
        <p className="mt-2 font-serif text-[16px] leading-relaxed text-ink-2">
          Sign in as the site’s editor to let Claude work on the site.
        </p>
        <Link href={`/login?next=${encodeURIComponent(here)}`} className="mt-6 inline-block rounded-md bg-accent px-4 py-2 text-[14px] font-medium text-white">
          Sign in
        </Link>
      </>
    );
  } else if (!isAdmin) {
    body = (
      <>
        <h1 className="font-serif text-[28px] font-semibold">Connect Claude</h1>
        <p className="mt-2 font-serif text-[16px] leading-relaxed text-ink-2">
          Only the site’s editor can connect Claude to it.
        </p>
      </>
    );
  } else if (request.isLoading) body = <p className="text-[13px] text-ink-3">Loading…</p>;
  else if (request.error || !request.data) {
    body = (
      <>
        <h1 className="font-serif text-[28px] font-semibold">Connect Claude</h1>
        <p className="mt-2 font-serif text-[16px] leading-relaxed text-ink-2">
          {request.error?.message ?? "That request has expired or been answered. Start again from Claude."}
        </p>
      </>
    );
  } else {
    const { client, redirectHost } = request.data;
    body = (
      <>
        <h1 className="font-serif text-[28px] font-semibold">Connect {client}?</h1>
        <p className="mt-3 font-serif text-[16px] leading-relaxed text-ink-2">
          It will be able to read and change everything you can as the site’s editor: commentary, posts, the glossary,
          the bibliography, the counseling index and the site’s own pages, and to read the site’s statistics. It
          cannot delete a chapter or a post.
        </p>
        <p className="mt-3 text-[13px] text-ink-2">
          Allowing it sends you back to <span className="font-medium text-ink">{redirectHost}</span>. If you didn’t
          start this from Claude, cancel. You can disconnect it at any time from{" "}
          <Link href="/account" className="text-accent">your account</Link>.
        </p>
        {decide.error && <p className="mt-4 text-[13px] text-accent">{decide.error.message}</p>}
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => void answer(true)}
            disabled={decide.isPending || decide.isSuccess}
            className="rounded-md bg-accent px-4 py-2 text-[14px] font-medium text-white disabled:opacity-60"
          >
            Allow
          </button>
          <button
            onClick={() => void answer(false)}
            disabled={decide.isPending || decide.isSuccess}
            className="rounded-md border border-rule px-4 py-2 text-[14px] text-ink-2 hover:text-ink disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-md px-6 py-16">{body}</main>
    </div>
  );
}
